/**
 * The one way the web app talks to the API. Every path starts with the API prefix exactly once (nav.test.ts refuses a doubled prefix).
 * A 401 means the session has ended: the app is told, and shows sign-in. Errors carry the server's i18n key.
 *
 * PROVENANCE: the SIGNED_OUT_EVENT pattern of C:\Gap-Assessor\src\lib\api.ts @ 2023d32.
 * CHANGES (O1 web): PATCH, and an upload that sends the file as the body with the uploader's statement in the
 * X-Olivia-Document header (server/src/routes/client/documents.ts), so no multipart parser is needed and no title
 * travels in a URL.
 */

export const SIGNED_OUT_EVENT = 'olivia:signed-out'

/**
 * A refusal or failure from the server: its status, its code with the parameters the screen puts in the reader's
 * language (lib/errors.ts), and the i18n key. The message is the server's English sentence, for the console only: no
 * screen prints it (batch O-D, OLV-07).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly key: string,
    message: string,
    readonly params: Readonly<Record<string, string | number>> = {},
  ) {
    super(message)
  }
}

export function apiPath(path: string): string {
  const clean = path.replace(/^\/+/, '').replace(/^api\//, '')
  return `/api/${clean}`
}

async function send<T>(method: string, path: string, body: BodyInit | undefined, headers: Record<string, string>): Promise<T> {
  const res = await fetch(apiPath(path), { method, credentials: 'same-origin', headers, body })
  const text = await res.text()
  let data: unknown = null
  try {
    data = text ? JSON.parse(text) : null
  } catch {
    data = null
  }
  if (!res.ok) {
    const d = (data ?? {}) as { code?: string; key?: string; error?: string; params?: Record<string, string | number> }
    if (res.status === 401 && !path.startsWith('auth/') && !path.startsWith('/auth/')) window.dispatchEvent(new Event(SIGNED_OUT_EVENT))
    throw new ApiError(res.status, d.code ?? 'INTERNAL', d.key ?? 'errors.INTERNAL', d.error ?? res.statusText, d.params ?? {})
  }
  return data as T
}

function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  return send<T>(method, path, body === undefined ? undefined : JSON.stringify(body), body === undefined ? {} : { 'content-type': 'application/json' })
}

/** What an uploader says about a file (design 5.9: every upload states whether it holds customer-level data). */
export interface UploadStatement {
  title: string
  kind: string
  customerContent: 'none'
  originalName?: string | null
  versionOf?: string | null
  /** IP12: the intake batch (IB-nnn) the file goes into, to wait in its analysis's evidence inbox. */
  batch?: string | null
}

/** What is said about an answered questionnaire sent back (IP15f): only whether it holds customer-level data. */
export interface QuestionnaireStatement {
  customerContent: 'none'
  originalName: string | null
}

/** What is said about an interview's transcript file, sent with it to the interview (never as an ordinary upload). */
export interface TranscriptStatement {
  title: string
  tool: string
  speakerMap: Record<string, string>
  customerContent: 'none'
  originalName: string | null
  heldOn: string | null
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  /**
   * A file for one client. The statement type admits only 'none': a customer-level file is refused in the
   * browser before anything is sent (customer-level documents come with their controls in O2b), and the server
   * refuses it again before reading the body.
   */
  upload: <T>(path: string, file: Blob, statement: UploadStatement) =>
    send<T>('POST', path, file, {
      'content-type': 'application/octet-stream',
      'x-olivia-document': encodeURIComponent(JSON.stringify(statement)),
    }),
  /** An interview's transcript file: the server checks every participant's consent before it reads the bytes. */
  uploadTranscript: <T>(path: string, file: Blob, statement: TranscriptStatement) =>
    send<T>('POST', path, file, {
      'content-type': 'application/octet-stream',
      'x-olivia-document': encodeURIComponent(JSON.stringify(statement)),
    }),
  /** An answered questionnaire (IP15f): the server reads it strictly and records each answer as a self-rating. */
  uploadQuestionnaire: <T>(path: string, file: Blob, statement: QuestionnaireStatement) =>
    send<T>('POST', path, file, {
      'content-type': 'application/octet-stream',
      'x-olivia-document': encodeURIComponent(JSON.stringify(statement)),
    }),
  /**
   * A POST answered as server-sent events (IP10a, a module run; Gap Assessor's pattern): every frame `data: <json>` is
   * handed to onFrame as it arrives. A refusal before the stream opens is a JSON answer, thrown as an ApiError like any other.
   */
  stream: (path: string, body: unknown, onFrame: (frame: Record<string, unknown>) => void) => streamFrames(path, body, onFrame),
}

async function streamFrames(path: string, body: unknown, onFrame: (frame: Record<string, unknown>) => void): Promise<void> {
  const res = await fetch(apiPath(path), { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
  if (!res.ok) {
    let d: { code?: string; key?: string; error?: string; params?: Record<string, string | number> } = {}
    try {
      d = (await res.json()) as typeof d
    } catch {
      d = {}
    }
    if (res.status === 401) window.dispatchEvent(new Event(SIGNED_OUT_EVENT))
    throw new ApiError(res.status, d.code ?? 'INTERNAL', d.key ?? 'errors.INTERNAL', d.error ?? res.statusText, d.params ?? {})
  }
  let pending = ''
  const take = (chunk: string, end: boolean) => {
    pending += chunk
    const blocks = pending.split('\n\n')
    pending = end ? '' : (blocks.pop() ?? '')
    for (const b of blocks) {
      if (!b.startsWith('data: ')) continue
      try {
        onFrame(JSON.parse(b.slice(6)) as Record<string, unknown>)
      } catch {
        // a frame that is not JSON is not one the server sends; it is skipped
      }
    }
  }
  const reader = res.body?.getReader()
  if (!reader) {
    take(await res.text(), true)
    return
  }
  const decoder = new TextDecoder()
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    take(decoder.decode(value, { stream: true }), false)
  }
  take(decoder.decode(), true)
}
